/**
 * Where this visit came from.
 *
 * Captured ONCE, at first load, and kept for the tab. It cannot be read at
 * submit time, because `lib/router` navigates with
 * `history.pushState({}, '', '/politica-de-confidentialitate')` — a bare path,
 * which replaces the whole URL and destroys the query string. Anyone who opens
 * the policy in-tab before submitting would otherwise arrive at the server with
 * no campaign attached, and the ad that produced them would be unknowable.
 *
 * The result rides the `referrer` field that already exists end to end: client
 * → validation/payload.ts → SQLite → the notification email → the CSV export.
 * That is deliberate. A dedicated column would mean a schema migration on the
 * production database for a field whose entire job is to say "meta, concept b",
 * and the privacy policy already discloses this category of data as "pagina de
 * la care ați ajuns la formular".
 *
 * ALLOWLIST, not passthrough: only the tracking parameters below are kept.
 * Storing whatever happens to be in the query string would mean storing
 * arbitrary third-party input we never asked for and cannot account for.
 */

const STORAGE_KEY = 'prq.provenance.v1';

/** Server sanitises `referrer` at 500 characters; stay under it. */
const MAX_LENGTH = 460;

const TRACKING_PARAMS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  /* Meta and Google click identifiers. Meta appends fbclid itself, so this
     survives even when the destination URL was built without UTMs. */
  'fbclid',
  'gclid',
] as const;

function build(): string {
  const parts: string[] = [];

  try {
    const params = new URLSearchParams(window.location.search);
    const kept = TRACKING_PARAMS.flatMap((key) => {
      const value = params.get(key);
      return value ? [`${key}=${value}`] : [];
    });
    if (kept.length > 0) parts.push(kept.join('&'));
  } catch {
    /* Malformed query string. Provenance is diagnostic, never load-bearing:
       losing it must not cost the owner their submission. */
  }

  /*
   * Kept alongside, not instead. It is close to useless for Meta — in-app
   * browser traffic arrives as l.facebook.com at best, and iOS often strips it
   * entirely — but it is the only signal there is for organic and referral
   * visits, which is what it was doing here before.
   */
  if (document.referrer) parts.push(`ref: ${document.referrer}`);

  return parts.join(' | ').slice(0, MAX_LENGTH);
}

/**
 * Reads and stores provenance for this tab. Safe to call more than once: the
 * first call wins, so a re-render or a StrictMode double-invoke cannot overwrite
 * the real arrival with a later, emptier one.
 */
export function captureProvenance(): void {
  try {
    if (window.sessionStorage.getItem(STORAGE_KEY) !== null) return;
    window.sessionStorage.setItem(STORAGE_KEY, build());
  } catch {
    /* Safari private mode throws. See readProvenance for the fallback. */
  }
}

/** The stored value, or a fresh read if storage is unavailable. */
export function readProvenance(): string | null {
  try {
    const stored = window.sessionStorage.getItem(STORAGE_KEY);
    if (stored !== null) return stored || null;
  } catch {
    /* Fall through. */
  }
  /*
   * No storage: build it now. On the wizard route this is still correct, since
   * the URL has not been rewritten. It is only wrong for someone who visited
   * the policy first, which is the rarer loss of the two.
   */
  return build() || null;
}
